import { getAttrValue, getLocalizedValue, isString, getLocalizedAttrValue, getLocalizedAttrValues, getFrontImage } from "@grandvisionhq/utils";
import { getActiveVariant, getMinimumSupplement, getPrice } from "../../helpers.js";
const reduceVariantInfo = (locale) => (mem, variant) => {
  if (!variant.sku || !variant.isMatchingVariant || !variant.attributes) {
    return mem;
  }
  const variationAttribute = getAttrValue(variant, "variationAttribute");
  if (!variationAttribute || !isString(variationAttribute)) {
    return mem;
  }
  const title = getLocalizedAttrValue(variant, variationAttribute, locale);
  if (!title) {
    return mem;
  }
  const isDefault = getAttrValue(variant, "default") === true;
  return [
    ...mem,
    {
      sku: variant.sku,
      title,
      isDefault,
      ...variant.images && {
        image: variant.images[0],
        image_v2: variant.images[1]
      }
    }
  ];
};
const getVariants = (product, locale) => {
  const productHasOnlyMasterVariant = !product.variants.length;
  return productHasOnlyMasterVariant ? {} : {
    variants: [product.masterVariant, ...product.variants].reduce(
      reduceVariantInfo(locale),
      []
    )
  };
};
const getBaseInfo = (product, sku, locale) => {
  const variant = getActiveVariant(product, sku);
  const isMasterVariant = product.masterVariant.sku === variant.sku;
  const productHasOnlyMasterVariant = !product.variants.length;
  let isDefault = false;
  if (isMasterVariant && productHasOnlyMasterVariant) {
    isDefault = getAttrValue(variant, "default") === true;
  } else if (!isMasterVariant) {
    isDefault = getAttrValue(variant, "default") === true;
  }
  let rootImage;
  const [firstMasterImage, secondMasterImage] = product.masterVariant.images ?? [];
  const [variantImage] = variant.images ?? [];
  const image_v2 = productHasOnlyMasterVariant ? firstMasterImage : secondMasterImage;
  if (!isMasterVariant) {
    rootImage = variantImage;
  } else {
    rootImage = productHasOnlyMasterVariant ? firstMasterImage : secondMasterImage;
  }
  const discountDescription = variant.prices?.[0]?.discounted?.discount?.obj?.description;
  const discountText = discountDescription ? getLocalizedValue(discountDescription, locale) : void 0;
  const tags = product.masterVariant.tags?.map(({ key, value }) => ({ key, value: typeof value === "string" ? value : value[locale] })).filter(({ value }) => !!value);
  const colorCode = getAttrValue(variant, "colorCode");
  const transitionType = getAttrValue(variant, "transitionType");
  return {
    sku,
    title: getLocalizedValue(product.name, locale),
    description: product.description ? getLocalizedValue(product.description, locale) : void 0,
    price: getPrice(variant),
    minimumSupplement: getMinimumSupplement(variant),
    isDefault,
    colorCode,
    transitionType,
    ...tags && { tags },
    ...discountText && { discountText },
    ...rootImage && { image: rootImage },
    ...image_v2 && { image_v2 }
  };
};
const getUsps = (product, sku, locale) => {
  const variant = getActiveVariant(product, sku);
  return getLocalizedAttrValues(variant, "prescriptionGlassPackageUsps", locale);
};
const convertToFrameInfo = (product, sku) => {
  const variant = getActiveVariant(product, sku);
  const brand = typeof getAttrValue(variant, "brand") !== "object" ? String(getAttrValue(variant, "brand")) : "";
  const codeAttr = getAttrValue(variant, "brandModelCode");
  const code = typeof codeAttr === "string" ? codeAttr : "";
  const title = [brand, code].filter(Boolean).join(" ");
  return {
    price: getPrice(variant),
    sku,
    title
  };
};
const convertToLensTypeInfo = (product, sku, locale) => ({
  ...getBaseInfo(product, sku, locale),
  ...getVariants(product, locale)
});
const convertToPackageInfo = (product, sku, locale) => {
  const variant = getActiveVariant(product, sku);
  return {
    ...getBaseInfo(product, sku, locale),
    usps: getUsps(product, sku, locale),
    areAddOnsOptional: variant.attributes?.find((a) => a.name === "areAddonsOptional")?.value ?? true
  };
};
const convertToLensAddOnInfo = (product, sku, locale) => ({
  ...getBaseInfo(product, sku, locale),
  ...getVariants(product, locale),
  productKey: product.key
});
const lowToHigh = (extractPrice) => (left, right) => {
  return (extractPrice(left) || 0) - (extractPrice(right) || 0);
};
const getProductImageData = (product, sku, width) => {
  const variant = getActiveVariant(product, sku);
  const calculatedWidth = Math.floor(width);
  const image = getFrontImage(variant.media?.images);
  return {
    url: `${image.url}?w=${calculatedWidth}&fm=jpeg&auto=format`,
    dimensions: image.dimensions
  };
};
export {
  convertToFrameInfo,
  convertToLensAddOnInfo,
  convertToLensTypeInfo,
  convertToPackageInfo,
  getProductImageData,
  lowToHigh,
  reduceVariantInfo
};
//# sourceMappingURL=helpers.js.map
